import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  dailyTimesheets, type DailyTimesheetItem, type DayCode,
} from '@/api/timesheets';
import { orgs } from '@/api/catalogs';
import { refs } from '@/api/refs';
import { useConfirm } from '@/components/ConfirmProvider';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import { useFormValidation } from '@/hooks/useFormValidation';
import { useNumberPreview } from '@/hooks/useNumberPreview';
import DatePopover from '@/components/DatePopover';

/**
 * Денний табель — допоміжний документ для збору даних за один день.
 * Автопідтягування співробітників (без масового заповнення полів — години/код
 * вводяться вручну). Місячний табель потім збирається з денних.
 */
interface Form {
  organization_id: string;
  document_number: string;
  day_date: string;     // YYYY-MM-DD
  comment: string;
  items: DailyTimesheetItem[];
}

const TODAY = new Date().toISOString().slice(0, 10);

const EMPTY: Form = {
  organization_id: '', document_number: '', day_date: TODAY, comment: '', items: [],
};

const CODE_LABELS: Record<DayCode, string> = {
  W: 'Робота', V: 'Відпустка', S: 'Лікарняний', A: 'Прогул', OFF: 'Вихідний',
};

export default function DailyTimesheetEditor() {
  const confirm = useConfirm();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { id } = useParams();
  const isNew = !id || id === 'new';

  const [form, setForm] = useState<Form>(EMPTY);
  const [error, setError] = useState<string | null>(null);
  const [staffOnly, setStaffOnly] = useState(true);
  const v = useFormValidation();
  const numberPreview = useNumberPreview('doc.daily_timesheets', form.organization_id, isNew);

  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const {
    availableOrgs, hasPermissionInAnyOrg, loading: permsLoading,
  } = usePermissionsByOrg('payroll.manage');

  const docQuery = useQuery({
    queryKey: ['daily-timesheet', id],
    queryFn: () => dailyTimesheets.get(id!),
    enabled: !isNew,
  });

  const empQ = useQuery({
    queryKey: ['employees', 'daily-ts', form.organization_id, staffOnly],
    queryFn: () => refs.employees(form.organization_id, {
      ...(staffOnly ? { employment_type: 'STAFF' } : {}),
      active_only: true,
    }),
    enabled: !!form.organization_id,
  });

  useEffect(() => {
    if (isNew) {
      const first = availableOrgs?.[0]?.id ?? '';
      setForm((f) => f.organization_id ? f : { ...f, organization_id: first });
    } else if (docQuery.data) {
      const d = docQuery.data;
      setForm({
        organization_id: d.organization_id,
        document_number: d.document_number,
        day_date: d.day_date,
        comment: d.comment ?? '',
        items: d.items.map((it) => ({
          line_number: it.line_number, employee_id: it.employee_id,
          day_code: it.day_code, worked_hours: it.worked_hours, comment: it.comment,
        })),
      });
    }
  }, [isNew, docQuery.data, availableOrgs]);

  const create = useMutation({
    mutationFn: () => dailyTimesheets.create({
      organization_id: form.organization_id,
      document_number: form.document_number || null,
      day_date: form.day_date,
      comment: form.comment || null,
      items: form.items,
    }),
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['daily-timesheets'] });
      navigate(`/daily-timesheets/${doc.id}`);
    },
    onError: (e) => setError(extractError(e)),
  });
  const update = useMutation({
    mutationFn: () => dailyTimesheets.update(id!, {
      document_number: form.document_number,
      day_date: form.day_date,
      comment: form.comment || null,
      items: form.items,
    }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['daily-timesheet', id] }),
    onError: (e) => setError(extractError(e)),
  });
  const remove = useMutation({
    mutationFn: () => dailyTimesheets.remove(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['daily-timesheets'] });
      navigate('/timesheets');
    },
  });

  function onSave() {
    if (!v.check([
      ['organization_id', form.organization_id, 'Оберіть організацію'],
      ['day_date', form.day_date, 'Вкажіть дату'],
    ])) return;
    isNew ? create.mutate() : update.mutate();
  }

  // Автопідтягування співробітників (БЕЗ масового заповнення — код OFF, 0 годин).
  function fillEmployees() {
    if (!empQ.data || empQ.data.length === 0) return;
    setForm((f) => ({
      ...f,
      items: empQ.data!.map((e, idx) => ({
        line_number: idx + 1, employee_id: e.id,
        day_code: 'W' as DayCode, worked_hours: 8, comment: null,
      })),
    }));
  }

  function setItem(i: number, patch: Partial<DailyTimesheetItem>) {
    setForm((f) => ({
      ...f, items: f.items.map((e, idx) => idx === i ? { ...e, ...patch } : e),
    }));
  }
  function addRow() {
    setForm((f) => ({
      ...f, items: [...f.items, {
        line_number: f.items.length + 1, employee_id: '',
        day_code: 'W' as DayCode, worked_hours: 8, comment: null,
      }],
    }));
  }
  function removeRow(i: number) {
    setForm((f) => ({
      ...f, items: f.items.filter((_, idx) => idx !== i)
        .map((e, idx) => ({ ...e, line_number: idx + 1 })),
    }));
  }

  if (isNew && !permsLoading && !hasPermissionInAnyOrg) {
    return (
      <div className="card"><div style={{ padding: '1.5rem', textAlign: 'center' }}>
        <div style={{ padding: '1rem', background: '#fef3c7', borderRadius: 6, maxWidth: 600, margin: '1rem auto' }}>
          ⚠ Немає прав створювати табель у жодній з ваших організацій.
        </div>
        <button onClick={() => navigate('/timesheets')}>Назад</button>
      </div></div>
    );
  }

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>
          {isNew ? 'Новий денний табель' : `Денний табель № ${form.document_number}`}
        </h2>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button onClick={() => navigate('/timesheets')}>Назад</button>
          <button className="primary" onClick={onSave}
                  disabled={create.isPending || update.isPending}>Зберегти</button>
          {!isNew && (
            <button className="danger"
              onClick={() => confirm('Видалити денний табель?').then((ok) => { if (ok) remove.mutate(); })}>
              Видалити
            </button>
          )}
        </div>
      </div>

      {v.banner()}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.75rem' }}>
        <div className="field">
          <label>Організація <span style={{ color: 'var(--danger)' }}>*</span></label>
          <select value={form.organization_id} disabled={!isNew}
            onChange={(e) => { setForm({ ...form, organization_id: e.target.value }); v.clear('organization_id'); }}
            style={v.inputStyle('organization_id')}>
            <option value="">—</option>
            {(isNew ? availableOrgs ?? [] : orgList.data?.items ?? []).map((o) => (
              <option key={o.id} value={o.id}>{o.code} — {o.name}</option>
            ))}
          </select>
          {v.error('organization_id')}
        </div>
        <div className="field">
          <label>№ документа</label>
          <input value={form.document_number}
            placeholder={isNew && numberPreview ? `буде: ${numberPreview}` : ''}
            onChange={(e) => setForm({ ...form, document_number: e.target.value })} />
        </div>
        <div className="field">
          <label>Дата <span style={{ color: 'var(--danger)' }}>*</span></label>
          <DatePopover value={form.day_date}
            onChange={(d) => { setForm({ ...form, day_date: d }); v.clear('day_date'); }} />
          {v.error('day_date')}
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '1rem', flexWrap: 'wrap', gap: '0.5rem' }}>
        <h3 style={{ margin: 0 }}>Співробітники</h3>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
            <input type="checkbox" checked={!staffOnly}
              onChange={(e) => setStaffOnly(!e.target.checked)} />
            Включно з ЦПУ
          </label>
          {empQ.data && empQ.data.length > 0 && (
            <button onClick={fillEmployees}>
              ⇧ Підтягнути співробітників ({empQ.data.length})
            </button>
          )}
        </div>
      </div>
      <p style={{ color: '#64748b', fontSize: '0.85rem', marginTop: '-0.3rem' }}>
        Введіть для кожного співробітника код дня (робота/відпустка/лікарняний/прогул/
        вихідний) і години. Денний табель збирається у місячний кнопкою «Зібрати з денних».
      </p>

      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
        <thead>
          <tr style={{ background: '#f8fafc', textAlign: 'left', fontSize: '0.78rem' }}>
            <th style={{ padding: '0.4rem', width: 25 }}>#</th>
            <th style={{ padding: '0.4rem' }}>Співробітник</th>
            <th style={{ padding: '0.4rem', width: 150 }}>Код дня</th>
            <th style={{ padding: '0.4rem', width: 100 }}>Години</th>
            <th style={{ padding: '0.4rem' }}>Коментар</th>
            <th style={{ padding: '0.4rem', width: 35 }}></th>
          </tr>
        </thead>
        <tbody>
          {form.items.map((it, i) => (
            <tr key={i} style={{ borderTop: '1px solid #e2e8f0' }}>
              <td style={{ padding: '0.4rem' }}>{it.line_number}</td>
              <td style={{ padding: '0.4rem' }}>
                <select value={it.employee_id}
                  onChange={(e) => setItem(i, { employee_id: e.target.value })}
                  style={{ width: '100%' }}>
                  <option value="">—</option>
                  {empQ.data?.map((e) => (
                    <option key={e.id} value={e.id}>{e.code ? `${e.code} — ` : ''}{e.name}</option>
                  ))}
                </select>
              </td>
              <td style={{ padding: '0.4rem' }}>
                <select value={it.day_code}
                  onChange={(e) => {
                    const c = e.target.value as DayCode;
                    setItem(i, { day_code: c, worked_hours: c === 'W' ? (Number(it.worked_hours) || 8) : 0 });
                  }}
                  style={{ width: '100%' }}>
                  {(Object.keys(CODE_LABELS) as DayCode[]).map((c) => (
                    <option key={c} value={c}>{CODE_LABELS[c]}</option>
                  ))}
                </select>
              </td>
              <td style={{ padding: '0.4rem' }}>
                <input type="number" step="0.5" min={0} max={24}
                  value={it.worked_hours} disabled={it.day_code !== 'W'}
                  onChange={(e) => setItem(i, { worked_hours: e.target.value })}
                  style={{ textAlign: 'right', width: '100%' }} />
              </td>
              <td style={{ padding: '0.4rem' }}>
                <input value={it.comment ?? ''}
                  onChange={(e) => setItem(i, { comment: e.target.value })}
                  style={{ width: '100%' }} />
              </td>
              <td style={{ padding: '0.4rem' }}>
                <button onClick={() => removeRow(i)} className="danger"
                  style={{ padding: '0.3rem 0.5rem' }}>×</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <button onClick={addRow} style={{ marginTop: '0.75rem' }}>+ Додати рядок</button>

      <div className="field" style={{ marginTop: '1rem' }}>
        <label>Коментар</label>
        <input value={form.comment}
          onChange={(e) => setForm({ ...form, comment: e.target.value })} />
      </div>

      {error && <div className="error">{error}</div>}
    </div>
  );
}

function extractError(e: unknown): string {
  if (typeof e === 'object' && e && 'response' in e) {
    const r = (e as { response?: { data?: { detail?: string } } }).response;
    return r?.data?.detail ?? 'Error';
  }
  return String(e);
}
